import { useQueryClient } from "@tanstack/react-query";
import { FC } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  getGetTemplateListQueryKey,
  useDeleteTemplate,
} from "~/api/custom-reports/custom-reports";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import { useOrganizationParams } from "~/helpers";
import { useToast } from "~/hooks/use-toast";
import { trackEvent } from "~/utils/analytics";

interface DeleteTemplateAlertDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  templateId: string;
  templateName: string;
}

export const DeleteTemplateAlertDialog: FC<DeleteTemplateAlertDialogProps> = ({
  open,
  setOpen,
  templateId,
  templateName,
}) => {
  const { organizationId } = useOrganizationParams();
  const { mutateAsync: deleteTemplate } = useDeleteTemplate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const intl = useIntl();

  const onDeleteTemplate = async () => {
    trackEvent({
      eventName:
        "app/settings_reports/delete_template_modal/delete.button/click",
    });
    try {
      // API currently expects only organizationId (per generated client)
      await deleteTemplate({ organizationId, templateId });
      await queryClient.invalidateQueries({
        queryKey: getGetTemplateListQueryKey(organizationId),
      });
      setOpen(false);
    } catch (_error) {
      toast({
        title: intl.formatMessage({
          id: "deleteTemplate.error",
          defaultMessage: "Failed to delete template",
        }),
      });
    }
  };

  const handleCancel = () => {
    trackEvent({
      eventName:
        "app/settings_reports/delete_template_modal/cancel.button/click",
    });
    setOpen(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            <FormattedMessage
              id="settings.deleteTemplateAlertDialog.title"
              defaultMessage="Delete Template"
            />
          </AlertDialogTitle>
          <AlertDialogDescription>
            <FormattedMessage
              id="settings.deleteTemplateAlertDialog.description"
              defaultMessage={
                "Are you sure you want to delete this {templateName} template?"
              }
              values={{ templateName: <strong>{templateName}</strong> }}
            />
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleCancel}>
            <FormattedMessage id="cancel" defaultMessage="Cancel" />
          </AlertDialogCancel>
          <Button variant="destructive" onClick={onDeleteTemplate}>
            <FormattedMessage id="delete" defaultMessage="Delete" />
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
